import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';
import { getAuth } from 'firebase/auth';
import EditApplicationModal from './EditApplicationModal';

interface ApplicationDetailsModalProps {
  isOpen: boolean;
  applicationId: string;
  onClose: () => void;
}

interface ApplicationData {
  applicationId: string;
  studentName: string;
  studentDOB: string;
  gender: string;
  religion: string;
  motherTongue: string;
  aadharNo: string;
  parentName: string;
  motherName: string;
  parentEmail: string;
  parentPhone: string;
  fatherProfession: string;
  applyingForGrade: string;
  previousSchool: string;
  lastClassAttended: string;
  address: string;
  status: string;
  reviewStatus: string;
  feesPaid: boolean;
  submittedAt: string;
  branchId: string;
  [key: string]: any;
}

const ApplicationDetailsModal: React.FC<ApplicationDetailsModalProps> = ({
  isOpen,
  applicationId,
  onClose
}) => {
  const [application, setApplication] = useState<ApplicationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (isOpen && applicationId) {
      fetchApplicationDetails();
    }
  }, [isOpen, applicationId]);

  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setApplication(data.data);
      } else {
        setError(data.error || 'Failed to load application');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (showEditModal) {
    return (
      <EditApplicationModal
        isOpen={showEditModal}
        applicationId={applicationId}
        onClose={() => setShowEditModal(false)}
        onSaved={() => {
          setShowEditModal(false);
          fetchApplicationDetails();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Application Details</h2>
            <p className="text-gray-600 text-sm mt-1">ID: {applicationId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="ml-4 text-gray-600">Loading application details...</p>
          </div>
        ) : application ? (
          <div className="space-y-6">
            {/* Status Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-600 uppercase">Status</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {application.status?.replace('_', ' ')}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-600 uppercase">Review Status</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {application.reviewStatus?.replace('_', ' ')}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-600 uppercase">Fees</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {application.feesPaid ? '✓ Paid' : '✗ Pending'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-600 uppercase">Submitted</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {new Date(application.submittedAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Student Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Student Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Student Name</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.studentName}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Date of Birth</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.studentDOB}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Gender</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.gender}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Religion</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.religion || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Mother Tongue</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.motherTongue || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Aadhar Number</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.aadharNo || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Applying for Grade</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.applyingForGrade}</p>
                </div>
              </div>
            </div>

            {/* Parent/Guardian Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Parent/Guardian Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Father Name</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.parentName}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Mother Name</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.motherName || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Father Profession</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.fatherProfession || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Email Address</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.parentEmail || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Phone Number</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.parentPhone}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Present Address</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.address || '-'}</p>
                </div>
              </div>
            </div>

            {/* Fee Structure Display */}
            {application.feeStructure && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Structure</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase">Monthly Fee</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">₹{application.feeStructure.tuitionFee}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase">Admission</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">₹{application.feeStructure.admissionFeeMin}-{application.feeStructure.admissionFeeMax}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase">Uniform</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">₹{application.feeStructure.uniformFee}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase">Books</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">₹{application.feeStructure.booksAndCopies}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Previous School Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Previous School Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Previous School</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.previousSchool || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-600 uppercase">Last Class Attended</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">{application.lastClassAttended || '-'}</p>
                </div>
              </div>
            </div>

            {/* Branch Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-600 uppercase">Branch</p>
              <p className="text-base font-semibold text-gray-900 mt-1">{application.branchId}</p>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              {application.status === 'enrolled' && (
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const auth = getAuth();
                      const token = await auth.currentUser?.getIdToken();
                      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/sync-erp`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await response.json();
                      if (data.success) {
                        alert('✅ Successfully synced to ERP!');
                      } else {
                        alert('❌ Sync failed: ' + data.error);
                      }
                    } catch (err: any) {
                      alert('❌ Sync failed: ' + err.message);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 font-medium flex items-center gap-2"
                  disabled={loading}
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loading ? 'Syncing...' : 'Sync to ERP'}
                </button>
              )}
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium flex items-center gap-2"
              >
                <PencilIcon className="h-4 w-4" />
                Edit Details
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ApplicationDetailsModal;
